import * as THREE from 'three';
import { RIG } from './PlayerModel';
import { strideLength } from './Motion';
import { PUCK_R } from './constants';

// ─── Math helpers ─────────────────────────────────────────────────────────────
const TAU = Math.PI * 2;
const clamp  = (v, a, b) => Math.min(b, Math.max(a, v));
const lerp   = (a, b, t) => a + (b - a) * t;
const frac   = x => x - Math.floor(x);
const smooth = (e0, e1, x) => { const t = clamp((x - e0) / (e1 - e0), 0, 1); return t * t * (3 - 2 * t); };
const damp   = (cur, target, lambda, dt) => lerp(cur, target, 1 - Math.exp(-lambda * dt));
const wrapAngle = a => { while (a > Math.PI) a -= TAU; while (a < -Math.PI) a += TAU; return a; };
const dampAngle = (cur, target, lambda, dt) => cur + wrapAngle(target - cur) * (1 - Math.exp(-lambda * dt));
const catmull = (p0, p1, p2, p3, t) => {
    const t2 = t * t, t3 = t2 * t;
    return 0.5 * (2 * p1 + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 + (-p0 + 3 * p1 - 3 * p2 + p3) * t3);
};

const BLADE_Z = RIG.BLADE_H / 2 + 0.004;
const GOALIE_REACH = 1.14;          // grip → heel on the goalie stick
const HAND0 = [0.12, 0.25, 0.09];   // top hand rest (spine space)

// ─── Stick choreography ───────────────────────────────────────────────────────
// s ∈ [-1, 0] is the wind-up (ending at release/impact), s ∈ [0, 1] the follow-through.
// Columns: s, blade x, y, z (root space), torso twist, top hand x, y, z (spine space)
export const ACTION_TIMING = {
    pass:    { windup: 0.30, follow: 0.32 },
    wrist:   { windup: 0.42, follow: 0.45 },
    slap:    { windup: 0.62, follow: 0.50 },
    receive: { windup: 0.32, follow: 0.30 },
};
const ACTION_KEYS = {
    pass: [
        [-1.00, -0.28, 0.74, BLADE_Z,  0.00, ...HAND0],
        [-0.45, -0.50, 0.14, BLADE_Z,  0.36, 0.15, 0.20, 0.10],
        [ 0.00, -0.34, 0.80, BLADE_Z, -0.06, ...HAND0],
        [ 0.45, -0.08, 1.02, 0.14,    -0.30, 0.06, 0.31, 0.13],
        [ 1.00, -0.28, 0.74, BLADE_Z,  0.00, ...HAND0],
    ],
    wrist: [
        [-1.00, -0.28, 0.74, BLADE_Z,  0.00, ...HAND0],
        [-0.40, -0.62, -0.06, BLADE_Z, 0.52, 0.17, 0.16, 0.12],
        [ 0.00, -0.36, 0.82, BLADE_Z, -0.06, 0.12, 0.27, 0.10],
        [ 0.45,  0.02, 1.04, 0.85,    -0.52, -0.02, 0.32, 0.30],
        [ 1.00, -0.28, 0.74, BLADE_Z,  0.00, ...HAND0],
    ],
    slap: [
        [-1.00, -0.28, 0.74, BLADE_Z,  0.00, ...HAND0],
        [-0.62, -0.58, -0.18, 0.60,    0.45, 0.10, 0.12, 0.28],
        [-0.24, -0.48, -0.55, 1.45,    0.78, 0.05, 0.03, 0.42],
        [ 0.00, -0.40, 0.66, BLADE_Z, -0.05, ...HAND0],
        [ 0.40,  0.16, 1.00, 1.10,    -0.62, -0.03, 0.30, 0.36],
        [ 1.00, -0.28, 0.74, BLADE_Z,  0.00, ...HAND0],
    ],
    receive: [
        [-1.00, -0.28, 0.74, BLADE_Z,  0.00, ...HAND0],
        [-0.35, -0.30, 0.88, BLADE_Z,  0.05, ...HAND0],
        [ 0.00, -0.30, 0.86, BLADE_Z,  0.05, ...HAND0],
        [ 0.40, -0.36, 0.50, BLADE_Z,  0.12, ...HAND0],
        [ 1.00, -0.28, 0.74, BLADE_Z,  0.00, ...HAND0],
    ],
};

function sampleKeys(keys, s, out) {
    let i = 0;
    while (i < keys.length - 2 && s > keys[i + 1][0]) i++;
    const k1 = keys[i], k2 = keys[i + 1];
    const k0 = keys[Math.max(0, i - 1)], k3 = keys[Math.min(keys.length - 1, i + 2)];
    const u = clamp((s - k1[0]) / (k2[0] - k1[0]), 0, 1);
    for (let c = 1; c < k1.length; c++) out[c - 1] = catmull(k0[c], k1[c], k2[c], k3[c], u);
    return out;
}

// ─── Scratch objects (shared, the animator is single-threaded) ────────────────
const _A = new THREE.Vector3(), _D = new THREE.Vector3(), _P = new THREE.Vector3();
const _K = new THREE.Vector3(), _E = new THREE.Vector3(), _T = new THREE.Vector3();
const _v1 = new THREE.Vector3(), _v2 = new THREE.Vector3(), _v3 = new THREE.Vector3();
const _q1 = new THREE.Quaternion(), _q2 = new THREE.Quaternion(), _rootQ = new THREE.Quaternion();
const _rootQi = new THREE.Quaternion();
const _m = new THREE.Matrix4();
const _e = new THREE.Euler(0, 0, 0, 'ZYX');
const DOWN = new THREE.Vector3(0, 0, -1);
const SHAFT_DOWN = new THREE.Vector3(0, -1, 0);
const UP = new THREE.Vector3(0, 0, 1);

// Point a bone (that hangs along local -Z) along a world direction.
// The minimal rotation is computed in the character's own frame so limb twist
// follows the player's heading.
function aimBone(bone, dirW) {
    _v1.copy(dirW).applyQuaternion(_rootQi);
    _q1.setFromUnitVectors(DOWN, _v1);
    _q1.premultiply(_rootQ);
    bone.parent.getWorldQuaternion(_q2);
    bone.quaternion.copy(_q2.invert().multiply(_q1));
}

// Analytic two-bone IK in world space. poleW is a direction the middle joint bends toward.
function solveTwoBone(upper, lower, targetW, poleW, L1, L2) {
    upper.getWorldPosition(_A);
    _D.subVectors(targetW, _A);
    const dist = clamp(_D.length(), Math.abs(L1 - L2) + 1e-3, (L1 + L2) * 0.999);
    _D.normalize();
    const a = (L1 * L1 - L2 * L2 + dist * dist) / (2 * dist);
    const h = Math.sqrt(Math.max(0, L1 * L1 - a * a));
    _P.copy(poleW).addScaledVector(_D, -poleW.dot(_D));
    if (_P.lengthSq() < 1e-8) _P.set(0, 0, 1).addScaledVector(_D, -_D.z);
    _P.normalize();
    _K.copy(_A).addScaledVector(_D, a).addScaledVector(_P, h);
    _E.copy(_A).addScaledVector(_D, dist);
    aimBone(upper, _v2.subVectors(_K, _A).normalize());
    aimBone(lower, _v3.subVectors(_E, _K).normalize());
}

// ─── SkaterAnimator ───────────────────────────────────────────────────────────
// Poses one articulated player every frame from its motion state.
// Locomotion is driven by the distance-integrated stride phase, so the cadence
// always matches ground speed and poses are deterministic when scrubbing.
export default class SkaterAnimator {
    constructor(rig) {
        this.rig = rig;
        this.isGoalie = rig.isGoalie;
        this.seed = Math.random() * 100;
        this.s = {
            amp: 0, back: 0, stop: 0, cross: 0, lean: 0, k: 0, oneHand: 0,
            butterfly: 0, turnSign: 1, thF: 0, thB: 0, th: 0, neckYaw: 0, neckPitch: 0,
        };
        this.puckContact = new THREE.Vector3();
        this.bladeTip = new THREE.Vector3();
        this._feet = [{}, {}];
        this._keys = new Array(7).fill(0);
        this._handSpine = new THREE.Vector3();
        this._hipDrop = 0;
    }

    // ── Foot targets, all returned in a local frame (x right, y forward) ──────
    _footForward(side, phase, v, aLong, out) {
        const st = this.s;
        const amp = st.amp, k = st.k, c = st.cross;
        const P = 0.56;
        const psi = frac(phase + (side < 0 ? 0 : 0.5));
        const outside = side === st.turnSign;      // turnSign +1 = turning left → right foot is outside
        const w0 = 0.11;
        const wMax = 0.11 + 0.24 * amp + 0.12 * k * amp;
        const xStart = side * (outside ? lerp(w0, -0.10, c) : w0);       // outside foot lands crossed over
        const xEnd   = side * (outside ? wMax : lerp(wMax, -0.16, c));   // inside foot pushes under
        const yF = 0.05 + 0.08 * amp;
        const yB = -(0.04 + 0.2 * amp + 0.1 * k * amp);
        const lift = (0.05 + 0.07 * k) * amp + (outside ? 0.06 * c : 0);

        // Skate angle so the wheels roll along the push direction (no sliding)
        const rate = v / strideLength(v, Math.max(0, aLong));
        const dudt = rate / P;
        const gx = (xEnd - xStart) * dudt;
        const gy = v + (yB - yF) * dudt;
        const pushYaw = clamp(Math.atan2(-gx, Math.max(0.35, gy)), -0.95, 0.95);

        if (psi < P) {
            const u = psi / P;
            out.x = lerp(xStart, xEnd, u);
            out.y = lerp(yF, yB, u);
            out.z = 0;
            out.yaw = pushYaw;
            out.roll = -side * 0.34 * amp * Math.sin(Math.PI * u);   // drive on the inside edge
        } else {
            const u = (psi - P) / (1 - P);
            const e = u * u * (3 - 2 * u);
            const sn = Math.sin(Math.PI * u);
            out.x = lerp(xEnd, xStart, e);
            out.y = lerp(yB, yF, e) + (outside ? 0.1 * c : 0.03) * sn;
            out.z = lift * sn;
            out.yaw = pushYaw + (-side * 0.06 - pushYaw) * 0.75 * sn;
            out.roll = side * 0.05 * sn;
        }
        return out;
    }

    _footBackward(side, phase, out) {
        const amp = this.s.amp;
        const P = 0.6;
        const psi = frac(phase + (side < 0 ? 0 : 0.5));
        if (psi < P) {                                   // C-cut: heel carves out, foot drives forward
            const u = psi / P;
            out.x = side * (0.13 + 0.2 * amp * Math.sin(Math.PI * u));
            out.y = lerp(-0.07, -0.07 + 0.26 * amp, u);
            out.z = 0;
            out.yaw = side * 0.5 * amp * (1 - u);
            out.roll = -side * 0.22 * amp * Math.sin(Math.PI * u);
        } else {
            const u = (psi - P) / (1 - P);
            const e = u * u * (3 - 2 * u);
            out.x = side * 0.13;
            out.y = lerp(-0.07 + 0.26 * amp, -0.07, e);
            out.z = 0.035 * amp * Math.sin(Math.PI * u);
            out.yaw = side * 0.5 * amp * e;
            out.roll = 0;
        }
        return out;
    }

    _footStop(side, out) {
        // Hockey stop: body turns, both skates sideways, lead skate ahead in the travel direction
        out.x = side * 0.03;
        out.y = side > 0 ? 0.27 : -0.15;
        out.z = 0;
        out.yaw = 1.35;
        out.roll = -0.35;
        return out;
    }

    _footIdle(side, out) {
        if (this.isGoalie) {
            out.x = side * 0.36; out.y = 0.0; out.z = 0;
            out.yaw = -side * 0.35; out.roll = -side * 0.12;
        } else {
            out.x = side * 0.19; out.y = side < 0 ? 0.035 : -0.03; out.z = 0;
            out.yaw = -side * 0.2; out.roll = 0;
        }
        return out;
    }

    _footButterfly(side, out) {
        out.x = side * 0.6; out.y = -0.28; out.z = -0.08;
        out.yaw = side * 0.55; out.roll = -side * 1.05;
        return out;
    }

    // ─── Per-frame update ─────────────────────────────────────────────────────
    // f: { t, dt, clock, vx, vy, speed, accLong, accLat, phase, arc, puck (Vector3 world),
    //      carrier, action: {kind, s} | null, butterfly }
    update(f) {
        const R = RIG, rig = this.rig, st = this.s, dt = f.dt;
        const goalie = this.isGoalie;
        const h = rig.root.rotation.z;
        const ch = Math.cos(h), sh = Math.sin(h);
        const v = f.speed;
        const lx = f.vx * ch + f.vy * sh;
        const ly = -f.vx * sh + f.vy * ch;

        // ── Smoothed locomotion blends ────────────────────────────────────────
        st.amp  = damp(st.amp, smooth(0.25, 2.2, v) * (goalie ? 0.5 : 1), 6, dt);
        st.back = damp(st.back, !goalie && v > 0.6 && ly < -0.35 * v ? 1 : 0, 5, dt);
        st.stop = damp(st.stop, !goalie && v > 0.8 && f.accLong < -5 ? smooth(5, 12, -f.accLong) : 0, 8, dt);
        st.cross = damp(st.cross, smooth(3.5, 9, Math.abs(f.accLat)) * (1 - st.back) * (goalie ? 0 : 1), 6, dt);
        st.lean = damp(st.lean, clamp(Math.atan2(f.accLat, 9.8), -0.45, 0.45), 6, dt);
        st.k    = damp(st.k, clamp((v - 1) / 6, 0, 1) + clamp(f.accLong / 8, 0, 0.4), 4, dt);
        if (Math.abs(f.accLat) > 2) st.turnSign = Math.sign(f.accLat);
        st.butterfly = damp(st.butterfly, f.butterfly || 0, 14, dt);

        if (v > 0.2) {
            const th = Math.atan2(-lx, ly);                 // travel direction vs facing (+ = left)
            st.th  = dampAngle(st.th, th, 10, dt);
            st.thF = damp(st.thF, clamp(th, -0.9, 0.9), 10, dt);
            st.thB = damp(st.thB, clamp(wrapAngle(th - Math.PI), -0.9, 0.9), 10, dt);
        }

        const amp = st.amp;
        const stopW = amp * st.stop;
        const moveW = amp * (1 - st.stop);
        const wF = goalie ? 0 : moveW * (1 - st.back);
        const wB = goalie ? 0 : moveW * st.back;
        const wI = 1 - amp;
        const phase = f.phase;
        const cyc = Math.sin(TAU * phase);

        // ── Action (pass / shot / receive) ────────────────────────────────────
        const act = f.action;
        let actW = 0, actTwist = 0, hipDropT = 0;
        const keys = this._keys;
        if (act && ACTION_KEYS[act.kind]) {
            sampleKeys(ACTION_KEYS[act.kind], act.s, keys);
            actW = smooth(-1, -0.72, act.s) * (1 - smooth(0.72, 1, act.s));
            actTwist = keys[3] * actW;
            if (act.kind !== 'receive') hipDropT = 0.06 * Math.exp(-Math.pow(act.s / 0.3, 2));
        }
        this._hipDrop = damp(this._hipDrop, hipDropT, 12, dt);

        // ── Feet ──────────────────────────────────────────────────────────────
        const footTmp = this._footTmp || (this._footTmp = [{}, {}, {}, {}, {}]);
        [-1, 1].forEach((side, i) => {
            const out = this._feet[i];
            const idle = this._footIdle(side, footTmp[0]);
            out.x = idle.x * wI; out.y = idle.y * wI; out.z = idle.z * wI; out.yaw = idle.yaw * wI; out.roll = idle.roll * wI;

            const addRot = (src, ang, w) => {
                if (w < 1e-4) return;
                const c = Math.cos(ang), s = Math.sin(ang);
                out.x += (src.x * c - src.y * s) * w;
                out.y += (src.x * s + src.y * c) * w;
                out.z += src.z * w;
                out.yaw += (src.yaw + ang) * w;
                out.roll += src.roll * w;
            };
            if (goalie) {
                // Goalies shuffle: small lateral steps on top of the stance
                const g = this._footIdle(side, footTmp[1]);
                g.x += side * 0.06 * Math.max(0, Math.sin(TAU * phase + (side > 0 ? 0 : Math.PI)));
                g.z = 0.05 * Math.max(0, Math.sin(TAU * phase + (side > 0 ? 0 : Math.PI)));
                addRot(g, st.th * 0.2, moveW + stopW);
            } else {
                addRot(this._footForward(side, phase, v, f.accLong, footTmp[1]), st.thF, wF);
                addRot(this._footBackward(side, phase, footTmp[2]), st.thB, wB);
                addRot(this._footStop(side, footTmp[3]), st.th, stopW);
            }
            if (st.butterfly > 0.001) {
                const b = this._footButterfly(side, footTmp[4]);
                const w = st.butterfly;
                out.x = lerp(out.x, b.x, w); out.y = lerp(out.y, b.y, w); out.z = lerp(out.z, b.z, w);
                out.yaw = lerp(out.yaw, b.yaw, w); out.roll = lerp(out.roll, b.roll, w);
            }
        });

        // ── Pelvis ────────────────────────────────────────────────────────────
        const idleHip = goalie ? 0.70 : 0.82;
        const fwdHip  = 0.80 - 0.05 * amp - 0.05 * st.k - 0.02 * amp * (0.5 - 0.5 * Math.cos(2 * TAU * phase));
        let hipH = idleHip * wI + fwdHip * wF + 0.78 * wB + 0.70 * stopW + (goalie ? idleHip * moveW : 0);
        hipH = lerp(hipH, 0.36, st.butterfly) - this._hipDrop;

        const breathe = Math.sin(f.clock * 1.7 + this.seed);
        let px = 0.055 * wF * cyc + 0.015 * wI * Math.sin(f.clock * 0.9 + this.seed);
        px -= Math.tan(st.lean) * hipH * 0.55;
        let py = 0.03 * amp;
        // Lean back against the travel direction in a hockey stop
        py += -0.12 * stopW * Math.cos(st.th);
        px += 0.12 * stopW * Math.sin(st.th);
        const pelvisYaw = -0.09 * wF * cyc + 1.2 * stopW + actTwist * 0.3;

        rig.pelvis.position.set(px, py, hipH - R.HIP_Z);
        rig.pelvis.rotation.set(0, -st.lean * 0.4, pelvisYaw, 'XYZ');

        // ── Spine ─────────────────────────────────────────────────────────────
        const idlePitch = goalie ? 0.36 : 0.44;
        let pitch = idlePitch * wI + (0.42 + 0.3 * st.k) * wF + 0.22 * wB + 0.16 * stopW + (goalie ? idlePitch * moveW : 0);
        pitch = lerp(pitch, 0.1, st.butterfly) + 0.02 * breathe * wI + this._hipDrop * 1.5;
        const twist = 0.1 * wF * cyc + actTwist * 0.7 - 0.9 * stopW;
        rig.spine.rotation.set(-pitch, -st.lean * 0.6, twist, 'XYZ');

        // ── Head tracks the puck ──────────────────────────────────────────────
        rig.root.updateMatrixWorld(true);
        _T.copy(f.puck);
        rig.root.worldToLocal(_T);
        const lookYaw = clamp(wrapAngle(Math.atan2(-(_T.x - px), _T.y - py) - pelvisYaw - twist), -1.1, 1.1);
        st.neckYaw = damp(st.neckYaw, lookYaw, 8, dt);
        rig.neck.rotation.set(pitch * 0.85, st.lean * 0.5, st.neckYaw * 0.7, 'XYZ');
        rig.head.rotation.set(0, 0, st.neckYaw * 0.3);

        rig.root.updateMatrixWorld(true);
        rig.root.getWorldQuaternion(_rootQ);
        _rootQi.copy(_rootQ).invert();

        // ── Legs (IK to foot targets) ─────────────────────────────────────────
        [rig.legL, rig.legR].forEach((leg, i) => {
            const side = i === 0 ? -1 : 1;
            const ft = this._feet[i];
            _T.set(ft.x, ft.y, R.ANKLE_H + ft.z);
            rig.root.localToWorld(_T);
            const bf = st.butterfly;
            const py2 = pelvisYaw * 0.8;
            const pole = _v3.set(side * (0.15 + 0.3 * bf), 1, -0.6 * bf);
            pole.set(pole.x * Math.cos(py2) - pole.y * Math.sin(py2), pole.x * Math.sin(py2) + pole.y * Math.cos(py2), pole.z)
                .applyQuaternion(_rootQ);
            const poleW = _K.copy(pole);
            solveTwoBone(leg.hip, leg.knee, _T.clone(), poleW.clone(), R.THIGH, R.SHIN);
            // Skate orientation: yaw (toe angle) + roll (edge)
            _e.set(0, ft.roll, h + ft.yaw, 'ZYX');
            _q1.setFromEuler(_e);
            leg.knee.getWorldQuaternion(_q2);
            leg.ankle.quaternion.copy(_q2.invert().multiply(_q1));
        });

        // Wheels roll with distance travelled
        const spin = -(f.arc / R.WHEEL_R) * (st.back > 0.5 ? -1 : 1);
        for (let i = 0; i < rig.wheels.length; i++) rig.wheels[i].rotation.x = spin;

        // ── Stick + hands ─────────────────────────────────────────────────────
        this._poseStick(f, act, actW, keys, phase, wF);

        // Contact shadow follows the body
        rig.shadow.position.set(px, py, 0.008);
        const sc = 1 + 0.25 * amp + 0.35 * st.butterfly + (goalie ? 0.2 : 0);
        rig.shadow.scale.set(sc, sc, 1);
    }

    _poseStick(f, act, actW, keys, phase, wF) {
        const R = RIG, rig = this.rig, st = this.s, dt = f.dt;
        const goalie = this.isGoalie;
        const cyc = Math.sin(TAU * phase);

        // Bottom hand lets go and swings while skating hard without the puck
        const freeArm = !goalie && !f.carrier && !act && f.speed > 2.8;
        st.oneHand = damp(st.oneHand, freeArm ? 1 : 0, 4, dt);

        // Blade target (root space)
        let bx, by, bz = BLADE_Z;
        if (goalie) {
            bx = lerp(0.06, 0.0, st.butterfly);
            by = lerp(0.62, 0.48, st.butterfly);
        } else if (f.carrier) {
            const handle = 1 - 0.45 * st.amp;
            bx = -0.28 + 0.2 * handle * Math.sin(TAU * 1.45 * f.t + this.seed);
            by = 0.74 + 0.05 * Math.sin(TAU * 2.9 * f.t + this.seed);
        } else {
            bx = -0.3 + 0.03 * Math.sin(f.clock * 0.8 + this.seed);
            by = 0.72;
        }
        if (!goalie) {
            const oh = st.oneHand;
            bx = lerp(bx, -0.18 + 0.12 * cyc, oh);
            by = lerp(by, 0.82, oh);
            bz += 0.05 * oh;
        }

        // Top hand (spine space)
        const hand = this._handSpine;
        if (goalie) hand.set(lerp(0.3, 0.36, st.butterfly), 0.27, lerp(0.06, 0.12, st.butterfly));
        else hand.set(HAND0[0], HAND0[1], HAND0[2]);

        let twistBottom = 0.33;
        if (actW > 0) {
            bx = lerp(bx, keys[0], actW);
            by = lerp(by, keys[1], actW);
            bz = lerp(bz, keys[2], actW);
            hand.set(lerp(hand.x, keys[4], actW), lerp(hand.y, keys[5], actW), lerp(hand.z, keys[6], actW));
            if (act.kind === 'slap') twistBottom = lerp(0.33, 0.5, actW);
        }

        // Knob position in root space
        _T.copy(hand);
        rig.spine.localToWorld(_T);
        rig.root.worldToLocal(_T);
        const knob = _T.clone();

        // Shaft reaches the blade target height with fixed length
        const reach = goalie ? GOALIE_REACH : R.STICK_LEN;
        const dz = knob.z - bz;
        const r = Math.sqrt(Math.max(reach * reach - dz * dz, 0.0025));
        let hx = bx - knob.x, hy = by - knob.y;
        const hl = Math.hypot(hx, hy) || 1;
        hx /= hl; hy /= hl;
        const heel = _v1.set(knob.x + hx * r, knob.y + hy * r, bz).clone();
        const s = _v2.subVectors(heel, knob).normalize().clone();

        rig.stickShaft.position.copy(knob);
        rig.stickShaft.quaternion.setFromUnitVectors(SHAFT_DOWN, s);

        // Blade: continues from the heel at the lie angle, in the shaft's vertical plane
        const p = _v3.copy(UP).addScaledVector(s, -UP.dot(s));
        if (p.lengthSq() < 1e-6) p.set(hx, hy, 0);
        p.normalize();
        const bladeDir = s.clone().multiplyScalar(Math.SQRT1_2).addScaledVector(p, Math.SQRT1_2).normalize();
        const bUp = p.clone().addScaledVector(bladeDir, -p.dot(bladeDir)).normalize();
        const bN = new THREE.Vector3().crossVectors(bladeDir, bUp);
        _m.makeBasis(bN, bladeDir, bUp);
        rig.stickBlade.quaternion.setFromRotationMatrix(_m);
        rig.stickBlade.position.copy(heel);

        rig.root.updateMatrixWorld(true);

        // Puck sits against the forehand face of the blade
        const nSign = (bN.x * 0.5 + bN.y * 0.85) >= 0 ? 1 : -1;
        this.puckContact.set(nSign * (PUCK_R + 0.012), R.BLADE_LEN * 0.55, 0);
        rig.stickBlade.localToWorld(this.puckContact);
        this.bladeTip.set(0, R.BLADE_LEN * 0.5, 0);
        rig.stickBlade.localToWorld(this.bladeTip);

        // Arms: top hand on the knob, bottom hand down the shaft (or swinging free)
        const topW = knob.clone().addScaledVector(s, goalie ? 0 : 0.05);
        rig.root.localToWorld(topW);
        const poleR = new THREE.Vector3(0.75, -0.45, -0.45).applyQuaternion(_rootQ);
        const poleL = new THREE.Vector3(-0.75, -0.35, -0.45).applyQuaternion(_rootQ);

        const topArm = rig.armR, botArm = rig.armL;
        solveTwoBone(topArm.shoulder, topArm.elbow, topW, poleR, R.UPPER_ARM, R.FOREARM);

        let botW;
        if (goalie) {
            botW = new THREE.Vector3(lerp(-0.38, -0.44, st.butterfly), lerp(0.24, 0.3, st.butterfly), lerp(0.1, 0.16, st.butterfly));
            rig.spine.localToWorld(botW);
        } else {
            const onStick = knob.clone().addScaledVector(s, R.STICK_LEN * twistBottom);
            rig.root.localToWorld(onStick);
            const oh = st.oneHand;
            if (oh > 0.01) {
                const free = new THREE.Vector3(-0.28 - 0.06 * Math.max(0, -cyc), 0.08 - 0.24 * cyc * wF, 0.02 + 0.05 * Math.abs(cyc));
                rig.spine.localToWorld(free);
                onStick.lerp(free, oh);
            }
            botW = onStick;
        }
        solveTwoBone(botArm.shoulder, botArm.elbow, botW, poleL, R.UPPER_ARM, R.FOREARM);
    }
}
